import { FormattedMessage } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { iconCart } from '@sorare/core/src/atoms/icons/system/iconCart.ts';
import { iconGavel } from '@sorare/core/src/atoms/icons/system/iconGavel.ts';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt.ts';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
} from '@sorare/core/src/constants/__generated__/routes';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';

import type { MarketHeroTag } from '#src/components/market-home/MarketHero/index.tsx';
import { MarketHero } from '#src/components/market-home/MarketHero/index.tsx';
import { getMarketShopUrl } from '#src/lib/marketShopRoutes.ts';
import { useLeagueLaunches } from '#src/lib/newSeason.tsx';

import background from './assets/background.webp';
import backgroundBaseball from './assets/background_baseball.webp';
import backgroundBaseballMobile from './assets/background_baseball_mobile.webp';
import backgroundBoost from './assets/background_boost.webp';
import backgroundBoostMobile from './assets/background_boost_mobile.webp';
import backgroundMobile from './assets/background_mobile.webp';
import iconBoost from './assets/icon_boost.webp';

type Props = {
  rarity: Rarity;
  sport: Sport;
};

const tags: MarketHeroTag[] = [
  {
    icon: iconGavel,
    label: <FormattedMessage defaultMessage="Bid & win in Auctions" />,
  },
  {
    icon: iconLightningBolt,
    label: <FormattedMessage defaultMessage="Buy instantly, play faster" />,
  },
];

export const MarketHomeHero = ({ rarity, sport }: Props) => {
  const { lifecycle } = useLifecycle();
  const { scoutableSo5Competitions } = useSo5Competitions();
  const leagueLaunches = useLeagueLaunches();

  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';
  const selectedCompetition = scoutableSo5Competitions.find(
    competition => competition.slug === competitionSlug
  );
  const shopFilters = {
    sport,
    rarity,
    so5CompetitionSlug: selectedCompetition?.slug,
  };

  const launching =
    !!selectedCompetition &&
    leagueLaunches.some(
      leagueLaunch =>
        leagueLaunch.sport === sport &&
        (leagueLaunch.byRarity?.[rarity]?.competitionSlug ===
          selectedCompetition.slug ||
          selectedCompetition.competitions.some(
            competition => competition.slug === leagueLaunch.slug
          ))
    );

  const backgrounds = (() => {
    if (launching)
      return { desktop: backgroundBoost, mobile: backgroundBoostMobile };
    if (sport === Sport.BASEBALL)
      return { desktop: backgroundBaseball, mobile: backgroundBaseballMobile };
    return { desktop: background, mobile: backgroundMobile };
  })();

  const icon = (() => {
    if (launching) return <img src={iconBoost} alt="" />;
    if (selectedCompetition)
      return (
        <img
          src={selectedCompetition.iconUrl}
          alt={selectedCompetition.displayName}
        />
      );
    return <StarBall />;
  })();

  return (
    <MarketHero
      backgroundUrl={backgrounds.desktop}
      mobileBackgroundUrl={backgrounds.mobile}
      icon={icon}
      subtitle={
        <FormattedMessage defaultMessage="Scout. Sign. Dominate. Find the players who can change your game." />
      }
      primaryCta={{
        label: <FormattedMessage {...navLabels.auctions} />,
        icon: iconGavel,
        to: getMarketShopUrl(ANY_SPORT_MARKET_SHOP_AUCTIONS, shopFilters),
      }}
      secondaryCta={{
        label: <FormattedMessage {...transferMarket.instantBuy} />,
        icon: iconCart,
        to: getMarketShopUrl(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, shopFilters),
      }}
      tags={tags}
    />
  );
};
